import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import Logo from "./gatepass-logo.png";

const Header = ({ setAuthenticated }) => {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const currentPath = location.pathname;
  const [isOpen, setIsOpen] = useState(false);

  return (
    <header className="sticky top-0 bg-white/80 backdrop-blur-lg border-b border-blue-50/80 z-40 py-2.5 px-4 md:px-8 shadow-sm">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        {/* Left Side: Logo */}
        <div className="flex items-center gap-2">
          <img src={Logo} alt="logo" className="h-9 w-auto" />
        </div>

        {/* Center: Navigation Tabs (Desktop only) */}
        <nav className="hidden md:flex items-center bg-slate-100/50 p-1 rounded-2xl border border-slate-200/30">
          <Link
            to="/"
            className={`relative px-4 py-2 text-xs md:text-sm font-bold rounded-xl transition-colors duration-200 z-10 no-underline ${
              currentPath === "/" ? "text-[#4580d6]" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            {t("createNewGp")}
            {currentPath === "/" && (
              <motion.div
                layoutId="activeHeaderTab"
                className="absolute inset-0 bg-[#4580d6]/10 rounded-xl -z-10 border border-[#4580d6]/20"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
          </Link>
          <Link
            to="/gatepasslist"
            className={`relative px-4 py-2 text-xs md:text-sm font-bold rounded-xl transition-colors duration-200 z-10 no-underline ${
              currentPath === "/gatepasslist" ? "text-[#4580d6]" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            {t("gatePassList")}
            {currentPath === "/gatepasslist" && (
              <motion.div
                layoutId="activeHeaderTab"
                className="absolute inset-0 bg-[#4580d6]/10 rounded-xl -z-10 border border-[#4580d6]/20"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
          </Link>
        </nav>

        {/* Right Side: Switcher + Logout (Desktop only) */}
        <div className="hidden md:flex items-center gap-3">
          {/* Language Switcher */}
          <div className="flex items-center gap-1 bg-white border border-blue-50 p-1.5 rounded-2xl shadow-sm">
            <button
              type="button"
              onClick={() => setLanguage("en")}
              className={`px-3 py-1 text-[10px] md:text-xs font-bold rounded-xl transition-all ${
                language === "en" ? "bg-[#4580d6] text-white shadow-sm" : "text-slate-500 hover:text-[#4580d6]"
              }`}
            >
              English
            </button>
            <button
              type="button"
              onClick={() => setLanguage("hi")}
              className={`px-3 py-1 text-[10px] md:text-xs font-bold rounded-xl transition-all ${
                language === "hi" ? "bg-[#4580d6] text-white shadow-sm" : "text-slate-500 hover:text-[#4580d6]"
              }`}
            >
              हिन्दी
            </button>
          </div>

          {/* Logout Button */}
          <button
            type="button"
            onClick={() => setAuthenticated(false)}
            className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs md:text-sm font-bold text-slate-500 hover:text-rose-600 hover:bg-rose-50/50 rounded-xl border border-transparent hover:border-rose-100 transition-all duration-200"
            title="Logout"
          >
            <i className="fa fa-sign-out-alt text-sm"></i>
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden flex items-center justify-center w-9 h-9 rounded-xl bg-slate-100/60 border border-slate-200/30 text-slate-600 hover:text-slate-800 hover:bg-slate-100 transition-all duration-200"
          aria-label="Toggle Menu"
        >
          <i className={`fa ${isOpen ? "fa-xmark" : "fa-bars"} text-base`}></i>
        </button>
      </div>

      {/* Mobile Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="md:hidden overflow-hidden mt-2"
          >
            <div className="flex flex-col gap-3 py-3 border-t border-slate-100">
              {/* Navigation Links */}
              <div className="flex flex-col gap-1.5">
                <Link
                  to="/"
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-2.5 text-xs font-bold rounded-xl transition-colors duration-200 no-underline ${
                    currentPath === "/"
                      ? "bg-[#4580d6]/10 text-[#4580d6] border border-[#4580d6]/10"
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                  }`}
                >
                  {t("createNewGp")}
                </Link>
                <Link
                  to="/gatepasslist"
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-2.5 text-xs font-bold rounded-xl transition-colors duration-200 no-underline ${
                    currentPath === "/gatepasslist"
                      ? "bg-[#4580d6]/10 text-[#4580d6] border border-[#4580d6]/10"
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                  }`}
                >
                  {t("gatePassList")}
                </Link>
              </div>

              {/* Language Switcher */}
              <div className="flex items-center justify-between px-4 py-2 bg-slate-50/50 rounded-xl border border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Language / भाषा
                </span>
                <div className="flex items-center gap-1 bg-white border border-blue-50 p-1 rounded-xl shadow-sm">
                  <button
                    type="button"
                    onClick={() => setLanguage("en")}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all ${
                      language === "en" ? "bg-[#4580d6] text-white shadow-sm" : "text-slate-500 hover:text-[#4580d6]"
                    }`}
                  >
                    English
                  </button>
                  <button
                    type="button"
                    onClick={() => setLanguage("hi")}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all ${
                      language === "hi" ? "bg-[#4580d6] text-white shadow-sm" : "text-slate-500 hover:text-[#4580d6]"
                    }`}
                  >
                    हिन्दी
                  </button>
                </div>
              </div>

              {/* Logout Button */}
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setAuthenticated(false);
                }}
                className="flex items-center justify-center gap-2 w-full py-2.5 text-xs font-bold text-rose-600 bg-rose-50/50 hover:bg-rose-50 border border-rose-100/50 rounded-xl transition-all duration-200"
              >
                <i className="fa fa-sign-out-alt"></i>
                <span>Logout</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
